import { useSoftphone } from "@dialstack/sdk-react/softphone";
import { useEffect, useState } from "react";

import type { PresenceEntry, PresenceUpdate } from "@dialstack/sdk-webrtc";

import type { VoicePresence, VoiceStatus } from "services/dialstack";

// What the phone reports for one colleague over the softphone's own socket.
export type LivePresenceEntry = {
  status: PresenceEntry["status"];
  doNotDisturb: boolean;
};

// The softphone watches at most this many colleagues at once (the SDK's
// MAX_PRESENCE_TARGETS); the rest stay on the polled directory.
const MAX_WATCHED = 100;

// The phone's live signal folded into the directory's `presence` vocabulary,
// the way the backend's `_presence_by_user` does: DND is its own axis and
// outranks the line; the phone cannot tell an unreachable colleague from one a
// push would still wake, so an `offline` keeps whatever the directory said.
export function foldLivePresence(
  entry: LivePresenceEntry,
  polled: VoicePresence | null
): VoicePresence {
  if (entry.doNotDisturb || entry.status === "dnd") return "dnd";
  if (entry.status === "on_call") return "on_call";
  if (entry.status === "available" || entry.status === "away")
    return "available";
  return polled === "mobile" ? "mobile" : "offline";
}

// What to show for a colleague once the phone's live state is known, keeping
// what Suno knows on top of it (ENG-2022) exactly as the backend's
// `_suno_presence` ladder does: a phone on DND shows the calendar reason when
// there is one; a live call outranks everything; then the calendar; then a
// scribe recording; else the phone. The Suno-only facts are read back out of
// the last polled `status` (a calendar reason survives the phone going DND;
// a recording only shows when nothing outranked it).
export function liveStatus(
  entry: LivePresenceEntry,
  polledPresence: VoicePresence | null,
  polledStatus: VoiceStatus | null | undefined
): VoiceStatus {
  const phone = foldLivePresence(entry, polledPresence);
  const calendar =
    polledStatus === "with_patient" || polledStatus === "in_meeting"
      ? polledStatus
      : null;
  if (phone === "dnd") return calendar ?? phone;
  if (phone === "on_call") return phone;
  if (calendar) return calendar;
  return polledStatus === "recording" ? "recording" : phone;
}

// Watches colleagues' presence over the softphone's existing socket
// (`subscribePresence`, SDK 3.3.0) instead of re-reading the directory every
// few seconds. `live` turns true once the snapshot has arrived; until then —
// and whenever there is no phone, or the platform refuses the subscription —
// the caller keeps polling. Listeners come off on close; the server keeps the
// watch list, which the SDK exposes no way to release, so a later open just
// subscribes to the same ids again.
export default function useLivePresence(userIds: string[]): {
  entries: Record<string, LivePresenceEntry>;
  live: boolean;
} {
  const { webrtcPhone } = useSoftphone();
  const [entries, setEntries] = useState<Record<string, LivePresenceEntry>>({});
  const [live, setLive] = useState(false);
  // Stable across renders that hand over an equal list in a new array.
  const watchKey = [...new Set(userIds)].sort().slice(0, MAX_WATCHED).join(",");

  useEffect(() => {
    if (!webrtcPhone || !watchKey) {
      setLive(false);
      setEntries({});
      return undefined;
    }
    const onList = (list: PresenceEntry[]) => {
      setEntries(
        Object.fromEntries(
          list.map((entry) => [
            entry.userId,
            { status: entry.status, doNotDisturb: entry.doNotDisturb }
          ])
        )
      );
      setLive(true);
    };
    const onUpdate = (update: PresenceUpdate) => {
      setEntries((previous) => ({
        ...previous,
        [update.userId]: {
          status: update.status,
          doNotDisturb: update.doNotDisturb
        }
      }));
    };
    webrtcPhone.on("presenceList", onList);
    webrtcPhone.on("presenceUpdate", onUpdate);
    try {
      webrtcPhone.subscribePresence(watchKey.split(","));
    } catch {
      // Not connected, or the list was refused: the caller keeps polling.
    }
    return () => {
      webrtcPhone.off("presenceList", onList);
      webrtcPhone.off("presenceUpdate", onUpdate);
      setLive(false);
    };
  }, [webrtcPhone, watchKey]);

  return { entries, live };
}
